import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { stripeAPI } from '../../services/api';

const ChangeSeatsModal = ({ isOpen, onClose, currentSeats, onUpdateSuccess }) => {
  const [seats, setSeats] = useState(10);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Set default seat value when modal opens
  useEffect(() => {
    if (isOpen) {
      setSeats(Math.max(currentSeats || 10, 10));
      setPreviewData(null);
      setError(null);
    }
  }, [isOpen, currentSeats]);

  const handlePreview = async () => {
    if (seats < 10) {
      setError('Minimum 10 seats required for Path A.');
      return;
    }
    if (seats === currentSeats) {
      setError('Selected seat count matches your current seat count.');
      return;
    }

    setPreviewLoading(true);
    setError(null);
    setPreviewData(null);
    try {
      const res = await stripeAPI.previewPathASeatsUpdate(seats);
      if (res.data && res.data.success) {
        setPreviewData(res.data);
      }
    } catch (err) {
      console.error('Failed to preview seat count update:', err);
      setError(err.response?.data?.error || 'Failed to calculate proration preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewData) return;
    setUpdating(true);
    setError(null);
    try {
      const res = await stripeAPI.updatePathASeats(previewData.newSeats);
      if (res.data && res.data.success) {
        alert(`Successfully changed plan to ${previewData.newSeats} seats!`);
        onUpdateSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Failed to update seat count:', err);
      setError(err.response?.data?.error || 'Failed to update seats. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const isPreviewDisabled = seats === currentSeats || seats < 10 || previewLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Path A Plan" maxWidth="max-w-lg">
      <div className="space-y-6">
        <div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Adjust the block of active client seats on your Path A subscription. Adding seats lets you approve more clients immediately. Reducing seats applies credits to your next monthly bill.
          </p>
        </div>

        {error && (
          <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-2xl text-orange-500 text-xs font-bold flex items-start gap-2">
            <span className="material-symbols-outlined text-base shrink-0">warning</span>
            <span className="leading-normal">{error}</span>
          </div>
        )}

        {/* Seat Counter */}
        <div className="bg-[var(--off-white)] border border-[var(--border-light)] p-5 rounded-2xl flex items-center justify-between gap-4">
          <div>
            <label className="block text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-1">
              Path A Seats
            </label>
            <p className="text-xs text-[var(--text-secondary)] font-medium">Current: <span className="font-bold text-[var(--text-primary)]">{currentSeats} seats</span></p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setSeats(prev => Math.max(10, prev - 1));
                setPreviewData(null);
              }}
              disabled={seats <= 10 || updating || previewLoading}
              className="w-10 h-10 rounded-xl bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-primary)] flex items-center justify-center font-bold shadow-sm hover:border-[var(--tangerine)] transition-all disabled:opacity-40"
            >
              —
            </button>
            <input
              type="number"
              min="10"
              value={seats}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setSeats(isNaN(val) ? 10 : val);
                setPreviewData(null);
              }}
              disabled={updating || previewLoading}
              className="w-16 text-center bg-[var(--surface-white)] border border-[var(--border-light)] rounded-xl font-bold py-2 focus:ring-[var(--tangerine)] focus:border-[var(--tangerine)] text-sm shadow-sm"
            />
            <button
              onClick={() => {
                setSeats(prev => prev + 1);
                setPreviewData(null);
              }}
              disabled={updating || previewLoading}
              className="w-10 h-10 rounded-xl bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-primary)] flex items-center justify-center font-bold shadow-sm hover:border-[var(--tangerine)] transition-all disabled:opacity-40"
            >
              +
            </button>
          </div>
        </div>

        {/* Preview Calculations Button */}
        {!previewData && (
          <button
            onClick={handlePreview}
            disabled={isPreviewDisabled}
            className="w-full py-3 bg-[var(--tangerine)] hover:bg-orange-600 disabled:bg-gray-300 dark:disabled:bg-neutral-800 disabled:text-gray-500 dark:disabled:text-neutral-500 text-white font-bold rounded-2xl shadow shadow-tangerine/20 hover:-translate-y-0.5 disabled:transform-none disabled:opacity-50 transition-all text-sm flex items-center justify-center gap-2"
          >
            {previewLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <span className="material-symbols-outlined text-lg">calculate</span>
            )}
            {previewLoading ? 'Calculating Proration...' : 'Preview Changes & Due Amount'}
          </button>
        )}

        {/* Preview Details Result */}
        {previewData && (
          <div className="space-y-4 border-t border-[var(--border-light)]/40 pt-4 animate-in fade-in duration-200">
            <h4 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest">Proration Calculation Preview</h4>
            
            {/* Prorated Payment Card */}
            {previewData.prorationAmount > 0 ? (
              <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-2xl flex gap-3">
                <span className="material-symbols-outlined text-orange-500 text-xl shrink-0 mt-0.5">shopping_bag</span>
                <div>
                  <p className="text-xs font-bold text-[var(--text-primary)]">Prorated payment due today</p>
                  <p className="text-xl font-black text-orange-500 mt-1">
                    +${previewData.prorationAmount.toFixed(2)}
                  </p>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1.5 leading-normal">
                    You will be charged this amount immediately to cover the cost of adding {previewData.newSeats - previewData.currentSeats} seats for the remaining days of your current billing period.
                  </p>
                </div>
              </div>
            ) : previewData.prorationAmount < 0 ? (
              <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-2xl flex gap-3">
                <span className="material-symbols-outlined text-green-500 text-xl shrink-0 mt-0.5">account_balance_wallet</span>
                <div>
                  <p className="text-xs font-bold text-[var(--text-primary)]">Unused seat credit applied</p>
                  <p className="text-xl font-black text-green-500 mt-1">
                    -${Math.abs(previewData.prorationAmount).toFixed(2)}
                  </p>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1.5 leading-normal">
                    Reducing by {previewData.currentSeats - previewData.newSeats} seats creates an unused credit. This credit will be applied automatically as a discount on your next bill.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl flex gap-3">
                <span className="material-symbols-outlined text-[var(--text-secondary)] text-xl shrink-0 mt-0.5">info</span>
                <div>
                  <p className="text-xs font-bold text-[var(--text-primary)]">No immediate payment due</p>
                  <p className="text-xl font-black text-[var(--text-primary)] mt-1">$0.00</p>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1.5 leading-normal">
                    Adjusting your subscription doesn't require any immediate prorated charges or credit offsets today.
                  </p>
                </div>
              </div>
            )}

            {/* Next Billing Cycle Preview */}
            <div className="p-4 bg-[var(--off-white)] border border-[var(--border-light)] rounded-2xl space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">New monthly total</span>
                <span className="font-bold text-[var(--text-primary)]">${previewData.newSeats * 100}.00/mo</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Next bill amount</span>
                <span className="font-bold text-[var(--text-primary)]">${previewData.nextInvoiceTotal.toFixed(2)}</span>
              </div>
              {previewData.nextPaymentDate && (
                <div className="flex justify-between border-t border-[var(--border-light)]/40 pt-2 mt-2">
                  <span className="text-[var(--text-secondary)]">Next payment date</span>
                  <span className="font-bold text-[var(--text-primary)]">
                    {new Date(previewData.nextPaymentDate).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setPreviewData(null)}
                disabled={updating}
                className="flex-1 py-3 text-sm font-bold text-[var(--text-secondary)] bg-[var(--surface-white)] border border-[var(--border-light)] rounded-2xl hover:bg-[var(--off-white)] transition-colors disabled:opacity-50"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={updating}
                className="flex-1 py-3 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-2xl shadow shadow-green-600/10 hover:-translate-y-0.5 disabled:transform-none disabled:opacity-75 transition-all flex items-center justify-center gap-2"
              >
                {updating && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {updating ? 'Confirming...' : 'Confirm Plan Update'}
              </button>
            </div>
          </div>
        )}

        {/* Base Footer Cancel */}
        {!previewData && (
          <div className="flex justify-end pt-4 border-t border-[var(--border-light)]/40">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ChangeSeatsModal;
